import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import DashLayout from "@/components/DashLayout";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "@/components/ui/table";
import { Plus, Edit, Trash2, Star, ShieldCheck } from "lucide-react";
import { toast } from "sonner";

export default function SuperSchools() {
  const nav = useNavigate();
  const [items, setItems] = useState([]);

  const load = async () => { const { data } = await api.get("/schools", { params: { limit: 500 } }); setItems(data); };
  useEffect(() => { load(); }, []);

  const remove = async (id) => {
    if (!window.confirm("Delete this school?")) return;
    await api.delete(`/schools/${id}`); toast.success("Deleted"); load();
  };
  const toggleFeatured = async (s) => { await api.put(`/schools/${s.id}`, { ...s, featured: !s.featured }); load(); };

  const [seeding, setSeeding] = useState(false);
  const seedDemo = async () => {
    if (!window.confirm("Insert 30 demo schools across major Indian cities? Already-existing names will be skipped.")) return;
    setSeeding(true);
    try {
      const { data } = await api.post("/demo/seed-schools");
      toast.success(`Inserted ${data.inserted} new, skipped ${data.skipped}`);
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setSeeding(false); }
  };

  return (
    <DashLayout variant="super" title="Schools">
      <div className="flex justify-between items-center mb-6">
        <div className="text-sm text-slate-500">{items.length} total</div>
        <div className="flex gap-2">
          <Button onClick={seedDemo} disabled={seeding} variant="outline" data-testid="seed-schools-btn">
            {seeding ? "Seeding…" : "Seed 30 Demo Schools"}
          </Button>
          <Button onClick={() => nav("/super/schools/new")} className="bg-slate-900 hover:bg-slate-800" data-testid="new-school-btn"><Plus className="w-4 h-4 mr-2" />Add School</Button>
        </div>
      </div>

      <div className="bg-white rounded-lg border border-slate-200 overflow-hidden">
        <Table>
          <TableHeader><TableRow>
            <TableHead>Code</TableHead><TableHead>Name</TableHead><TableHead>City</TableHead>
            <TableHead>Board</TableHead><TableHead>Level</TableHead>
            <TableHead className="text-right">Fees (min)</TableHead>
            <TableHead className="text-right">Likes</TableHead>
            <TableHead>Status</TableHead><TableHead className="text-right">Actions</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {items.map((s) => (
              <TableRow key={s.id} data-testid={`row-school-${s.id}`}>
                <TableCell className="font-mono font-bold text-blue-700 whitespace-nowrap" data-testid={`school-code-cell-${s.id}`}>{s.code || "—"}</TableCell>
                <TableCell className="font-semibold">{s.name}</TableCell>
                <TableCell>{s.city}</TableCell>
                <TableCell>{s.board}</TableCell>
                <TableCell>{s.school_type}</TableCell>
                <TableCell className="text-right tabular-nums">₹{(s.fees_min || 0).toLocaleString()}</TableCell>
                <TableCell className="text-right tabular-nums text-red-600 font-semibold" data-testid={`school-likes-${s.id}`}>{s.likes_count || 0}</TableCell>
                <TableCell>
                  <div className="flex gap-1 flex-wrap">
                    {s.featured && <Badge className="bg-amber-400 text-slate-900">Featured</Badge>}
                    {s.verified && <Badge className="bg-emerald-600"><ShieldCheck className="w-3 h-3 mr-1" />Verified</Badge>}
                    {s.admission_open && <Badge className="bg-blue-600">Adm. Open</Badge>}
                  </div>
                </TableCell>
                <TableCell className="text-right">
                  <Button size="sm" variant="ghost" onClick={() => toggleFeatured(s)} data-testid={`feature-toggle-${s.id}`}><Star className={`w-4 h-4 ${s.featured ? "fill-amber-400 text-amber-500" : ""}`} /></Button>
                  <Button size="sm" variant="ghost" onClick={() => nav(`/super/schools/${s.id}/edit`)} data-testid={`edit-school-${s.id}`}><Edit className="w-4 h-4" /></Button>
                  <Button size="sm" variant="ghost" onClick={() => remove(s.id)} data-testid={`delete-school-${s.id}`}><Trash2 className="w-4 h-4 text-red-600" /></Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </DashLayout>
  );
}
